/** Validation Utilities */

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Validate username (alphanumeric, 3-20 chars)
 * @param {string} username - Username to validate
 * @returns {boolean}
 */
export const isValidUsername = (username) => {
  return /^[a-zA-Z0-9_]{3,20}$/.test(username);
};

/**
 * Validate password strength (min 6 chars)
 * @param {string} password - Password to validate
 * @returns {boolean}
 */
export const isValidPassword = (password) => {
  return password && password.length >= 6;
};

/**
 * Validate required field
 * @param {*} value - Value to check
 * @returns {boolean}
 */
export const isRequired = (value) => {
  return value !== null && value !== undefined && value !== '';
};

/**
 * Validate phone number (basic)
 * @param {string} phone - Phone number
 * @returns {boolean}
 */
export const isValidPhone = (phone) => {
  const regex = /^[\d\s\-\+\(\)]{7,}$/;
  return regex.test(phone);
};

/**
 * Validate URL
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Form validation helper
 * @param {object} formData - Form data to validate
 * @param {object} rules - Validation rules
 * @returns {object} Errors object
 */
export const validateForm = (formData, rules) => {
  const errors = {};

  for (const [field, rule] of Object.entries(rules)) {
    const value = formData[field];

    if (rule.required && !isRequired(value)) {
      errors[field] = `${field} is required`;
      continue;
    }

    if (rule.email && value && !isValidEmail(value)) {
      errors[field] = 'Invalid email format';
      continue;
    }

    if (rule.minLength && value && value.length < rule.minLength) {
      errors[field] = `Minimum ${rule.minLength} characters required`;
      continue;
    }

    if (rule.maxLength && value && value.length > rule.maxLength) {
      errors[field] = `Maximum ${rule.maxLength} characters allowed`;
      continue;
    }

    if (rule.pattern && value && !rule.pattern.test(value)) {
      errors[field] = rule.message || 'Invalid format';
      continue;
    }

    if (rule.custom && value) {
      const customError = rule.custom(value);
      if (customError) {
        errors[field] = customError;
      }
    }
  }

  return errors;
};
